<template>
  <b-card id="interval-tabs" class="shadow rounded">
    <b-card-title>Dosing Plan Options</b-card-title>
    <b-card-body>
      <p class="text-muted">
        Select required plan under Action column from table below. Recommendation and display will
        update with selection.
      </p>
      <div class="form-check form-switch float-end">
        <input id="switch" v-model="showAll" class="form-check-input" type="checkbox" />
        <label
          v-b-tooltip.hover="
            'Show all computed doses, including those outside the therapeutic target range. By default only doses within range are shown.'
          "
          for="switch"
          class="form-check-label"
          >Show All</label
        >
      </div>
      <b-tabs v-model:index="selectedIndex">
        <b-tab
          v-for="(tab, index) in tabs"
          :key="tab.title"
          :title="tab.title"
          :class="{ tab: true, 'tab-bordered': true }"
          @click="selectTab(index)"
        >
          <div class="tab-content">
            <div v-if="tab.title === 'Custom'">
              <CustomForm :sim="sim" />
            </div>
            <div v-else>
              <KeepAlive v-if="items.length > 0">
                <SimulationDoses :data="items" :method="tab.title" />
              </KeepAlive>
              <div v-else class="text-muted font-medium p-4">
                <p v-if="sim && sim.hasMeasured" class="text-danger">
                  No target plans to display - select Show All
                </p>
                <p v-else>
                  <span class="text-danger">No plans to display. <br /></span>
                  Add one or more measured concentrations for optimised dose plans. Best results are
                  with 2 or more concentrations (peak and trough) in one dose interval.
                </p>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </b-card-body>
  </b-card>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import SimulationDoses from '@/components/display/SimulationDoses.vue';
import CustomForm from '@/components/inputs/CustomForm.vue';
import { DeliveryMethod } from '@/models/Enums';
import { useDosingStore } from '@/stores/dosing';

defineOptions({ name: 'PlanOptions' });

const store = useDosingStore();
const selectedIndex = ref(0);
const tabs = ref<any[]>([]);
const showAll = ref(false);

const sim = computed(() => store.getSimulation);
const drug = computed(() => store.getDrug);

watch(drug, () => {
  loadTabs();
});

onMounted(() => {
  loadTabs();
});

const items = computed(() => {
  const allOptions = loadOptions();
  return showAll.value ? allOptions : allOptions.filter((option: any) => option._rowVariant);
});

function nformat(value: number, decimals: number): string {
  return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals).toFixed(decimals);
}

function loadTabs() {
  if (drug.value) {
    const drugTabs = Object.keys(DeliveryMethod)
      .map((d) => ({ title: (DeliveryMethod as any)[d] }))
      .filter((tab) => drug.value.methods.some((method: any) => method.type === tab.title));
    tabs.value = drugTabs;
  } else {
    tabs.value = [];
  }
  tabs.value.push({ title: 'Custom' });
  selectedIndex.value = tabs.value.length - 1;
}

function loadOptions(): any[] {
  if (tabs.value.length > 0 && sim.value) {
    const selectedTab = tabs.value[selectedIndex.value];
    if (!selectedTab) return [];
    let selectedData = sim.value.plansForDeliveryMethod(selectedTab.title);

    // Filter to standard dose combinations when defined for this method
    const method = drug.value?.methods?.find((m: any) => m.type === selectedTab.title);
    if (method?.standardDoses?.length) {
      selectedData = selectedData.filter((s: any) =>
        method.standardDoses.some(
          (std: any) =>
            std.amount === s.doseRegime.dose &&
            std.infusionTime === s.doseRegime.infusionDuration &&
            std.interval === s.doseRegime.interval,
        ),
      );
    }

    return selectedData.map((s: any) => {
      if (s.doseRegime.deliveryMethod === 'CONT') {
        return {
          Dose: s.doseRegime.dose,
          doseUnit: 'mg',
          AUC: nformat(s.mainResult.auc, 2),
          aucUnit: 'mg·hr/L',
          SS: nformat(s.mainResult.ss, 2),
          ssUnit: 'mg/L',
          plan: s,
          _rowVariant: s._rowVariant,
        };
      } else if (sim.value.model.getCompartment() === 2) {
        return {
          Dose: s.doseRegime.dose,
          doseUnit: 'mg',
          Infusion: s.doseRegime.infusionDuration,
          InfusionUnit: 'h',
          Interval: s.doseRegime.interval,
          intervalUnit: 'h',
          TMIC: nformat(s.mainResult.tmic, 2),
          AUC: nformat(s.mainResult.auc, 2),
          aucUnit: 'mg·hr/L',
          Peak: nformat(s.mainResult.peak, 2),
          peakUnit: 'mg/L',
          Trough: nformat(s.mainResult.trough, 2),
          troughUnit: 'mg/L',
          plan: s,
          _rowVariant: s._rowVariant,
        };
      } else {
        return {
          Dose: s.doseRegime.dose,
          doseUnit: 'mg',
          Infusion: s.doseRegime.infusionDuration,
          InfusionUnit: 'h',
          Interval: s.doseRegime.interval,
          intervalUnit: 'h',
          AUC: nformat(s.mainResult.auc, 2),
          aucUnit: 'mg·hr/L',
          Peak: nformat(s.mainResult.peak, 2),
          peakUnit: 'mg/L',
          Trough: nformat(s.mainResult.trough, 2),
          troughUnit: 'mg/L',
          plan: s,
          _rowVariant: s._rowVariant,
        };
      }
    });
  }
  return [];
}

function selectTab(idx: number) {
  store.clearSelectedPlan();
  selectedIndex.value = idx;
}
</script>

<style scoped></style>
